import type { ItemId, Project } from '@finsim/model';
import type { RouteInstance, RouteParams } from 'atomic-router';
import { createStore, sample } from 'effector';
import { $locale } from '../../entities/preferences';
import { $project, projectClosed, projectShown } from '../../entities/project';
import {
  $focusedScenarioId,
  $selectedItem,
  itemSelected,
  scenarioFocused,
} from '../../entities/selection';
import {
  $hiddenScenarios,
  $simMode,
  $timeRange,
  $valueMode,
  $view,
  hiddenScenariosChanged,
  simModeChanged,
  timeRangeChanged,
  valueModeChanged,
  viewChanged,
  VIEWS,
  type View,
} from '../../entities/view-settings';
import { replaceQueryFx, router } from '../../shared/routing';

/*
 * Keeps view state in a project route's URL, so links are shareable and reloads restore it:
 * `?view=cashFlow&v=<variant>&item=<id>&mode=real&sim=mc&hide=<a>,<b>&range=12-240`.
 * A `lang` the URL already carries follows the language shown, so a reload keeps it.
 */
export function bindViewQuery<Params extends RouteParams>(route: RouteInstance<Params>): void {
  const entered = [route.opened, route.updated];

  // ── URL → state ─────────────────────────────────────────────────────────────
  sample({
    clock: entered,
    filter: ({ query }) => isView(query.view),
    fn: ({ query }) => query.view as View,
    target: viewChanged,
  });
  sample({
    clock: entered,
    fn: ({ query }) => (query.mode === 'real' ? ('real' as const) : ('nominal' as const)),
    target: valueModeChanged,
  });
  sample({
    clock: entered,
    fn: ({ query }) => (query.sim === 'mc' ? ('monteCarlo' as const) : ('deterministic' as const)),
    target: simModeChanged,
  });

  // Range, hidden variants and selection apply once the project is shown (showing resets them).
  const $pendingRange = createStore<[number, number] | null>(null)
    .on(entered, (_, { query }) => parseRange(query.range))
    .reset(projectClosed);
  sample({
    clock: projectShown,
    source: { range: $pendingRange, project: $project },
    filter: ({ range, project }) =>
      range !== null && project !== null && range[1] < project.assumptions.horizonMonths,
    fn: ({ range }) => range,
    target: timeRangeChanged,
  });

  /** `hide` lists the variants left out of the compare set (so variants added later are shown). */
  const $pendingHidden = createStore<string[] | null>(null)
    .on(entered, (_, { query }) =>
      typeof query.hide === 'string' ? query.hide.split(',').filter(Boolean) : null,
    )
    .reset(projectClosed);
  sample({
    clock: projectShown,
    source: $pendingHidden,
    filter: (hide) => hide !== null,
    fn: (hide, project) =>
      new Set(project.scenarios.map((s) => s.id).filter((id) => (hide ?? []).includes(id))),
    target: hiddenScenariosChanged,
  });

  const $pendingSelection = createStore<{ v?: string; item?: string }>({})
    .on(entered, (_, { query }) => ({
      ...(typeof query.v === 'string' ? { v: query.v } : {}),
      ...(typeof query.item === 'string' ? { item: query.item } : {}),
    }))
    .reset(projectClosed);
  const selectionFromUrl = sample({
    clock: [projectShown, $pendingSelection.updates],
    source: { project: $project, pending: $pendingSelection },
    filter: ({ project }) => project !== null,
    fn: ({ project, pending }) => resolveSelection(project as Project, pending),
  });
  sample({
    clock: selectionFromUrl.filterMap((s) =>
      s?.itemId ? { scenarioId: s.scenarioId, itemId: s.itemId } : undefined,
    ),
    target: itemSelected,
  });
  sample({
    clock: selectionFromUrl.filterMap((s) => (s && !s.itemId ? s.scenarioId : undefined)),
    target: scenarioFocused,
  });

  // ── state → URL ─────────────────────────────────────────────────────────────
  const desiredQuery = sample({
    clock: [
      $view,
      $valueMode,
      $simMode,
      $hiddenScenarios,
      $focusedScenarioId,
      $selectedItem,
      $timeRange,
      $locale,
      projectShown,
    ],
    source: {
      opened: route.$isOpened,
      project: $project,
      path: router.$path,
      current: router.$query,
      view: $view,
      mode: $valueMode,
      sim: $simMode,
      hidden: $hiddenScenarios,
      focused: $focusedScenarioId,
      selected: $selectedItem,
      range: $timeRange,
      locale: $locale,
    },
    filter: ({ opened, project }) => opened && project !== null,
    fn: (s) => {
      const query: Record<string, string> = {};
      if (s.view !== 'overview') query.view = s.view;
      if (s.focused) query.v = s.focused;
      if (s.selected) query.item = s.selected.itemId;
      if (s.mode === 'real') query.mode = 'real';
      if (s.sim === 'monteCarlo') query.sim = 'mc';
      const hide = (s.project?.scenarios ?? []).filter((v) => s.hidden.has(v.id)).map((v) => v.id);
      if (hide.length > 0) query.hide = hide.join(',');
      if (s.range) query.range = `${String(s.range[0])}-${String(s.range[1])}`;
      if (typeof s.current.lang === 'string') query.lang = s.locale;
      return { path: s.path, query, current: s.current };
    },
  });
  sample({
    clock: desiredQuery,
    filter: ({ query, current }) => !sameQuery(query, current),
    fn: ({ path, query }) => ({ path, query }),
    target: replaceQueryFx,
  });
}

const isView = (v: unknown): v is View =>
  typeof v === 'string' && (VIEWS as readonly string[]).includes(v);

/** `range=12-240` → [12, 240]. */
function parseRange(value: unknown): [number, number] | null {
  if (typeof value !== 'string') return null;
  const match = /^(\d+)-(\d+)$/.exec(value);
  if (!match) return null;
  const from = Number(match[1]);
  const to = Number(match[2]);
  return to > from ? [from, to] : null;
}

function resolveSelection(project: Project, pending: { v?: string; item?: string }) {
  const scenario = project.scenarios.find((s) => s.id === pending.v);
  if (!scenario) return null;
  const itemId = pending.item as ItemId | undefined;
  return {
    scenarioId: scenario.id,
    itemId: itemId && scenario.itemIds.includes(itemId) ? itemId : null,
  };
}

function sameQuery(a: Record<string, string>, b: Record<string, string>): boolean {
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((k) => a[k] === b[k]);
}
